/**
 * Per-character stem render trigger + status pill.
 *
 * Used inside the stems portal table. Combines the render mutation with the
 * server-reported status (`idle | queued | rendering | ready | failed`) and a
 * small "render new version" button. The realtime status update arrives via
 * the page's StemRender realtime channel; this component just reflects the
 * current status from the query cache.
 */
import { Badge, Button, Spinner } from '@ohear/ui'
import { useRenderCharacterStem } from '../../lib/api/stems'
import type { CharacterStem } from '../../lib/api/stems'

export interface StemRenderProps {
  projectId: string
  stem: CharacterStem
}

const STATUS_TONE: Record<CharacterStem['status'], 'gray' | 'blue' | 'amber' | 'green' | 'red'> = {
  idle: 'gray',
  queued: 'amber',
  rendering: 'amber',
  ready: 'green',
  failed: 'red',
}

const STATUS_LABEL: Record<CharacterStem['status'], string> = {
  idle: 'Not rendered',
  queued: 'Queued',
  rendering: 'Rendering',
  ready: 'Ready',
  failed: 'Failed',
}

export function StemRender({ projectId, stem }: StemRenderProps) {
  const render = useRenderCharacterStem(projectId)
  const busy = stem.status === 'queued' || stem.status === 'rendering' || render.isPending
  const tone = STATUS_TONE[stem.status]

  return (
    <div className="inline-flex items-center gap-2">
      <Badge variant={tone}>
        {busy ? <Spinner size="sm" /> : null}
        <span>{STATUS_LABEL[stem.status]}</span>
      </Badge>
      {stem.latest_version ? (
        <span className="text-[12px] text-[var(--text-2)] tabular-nums">
          v{stem.latest_version}
        </span>
      ) : null}
      <Button
        size="sm"
        variant="ghost"
        loading={busy}
        disabled={busy || stem.approved_takes === 0}
        onClick={() => render.mutate(stem.character_id)}
      >
        {stem.latest_version ? 'Re-render' : 'Render'}
      </Button>
    </div>
  )
}
